import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResourceOption } from '@kouto/types';
import {
  ResourceAttributePick,
  ResourceAttributeSelect,
} from 'components/ResourceAttributeSelect';
import {
  applyResourcePick,
  deriveCompatibleValueIds,
  deriveEarlierPickLabels,
  deriveResourceSlots,
  deriveSelectableValueIds,
  findResourcePick,
  ParticipantSelections,
  ResourceParticipant,
} from 'utils/resourceSelections/resourceSelections';
import * as SC from './ResourceAttributePicker.styled';

export interface ResourceAttributePickerProps {
  onChange: (value: ParticipantSelections) => void;
  options: ResourceOption[];
  participantIndex?: number;
  participants: ResourceParticipant[];
  portalContainer?: HTMLElement | null;
  value: ParticipantSelections;
}

const ResourceAttributePicker = ({
  onChange,
  options,
  participantIndex,
  participants,
  portalContainer,
  value,
}: ResourceAttributePickerProps) => {
  const { t: translate } = useTranslation();
  const slots = useMemo(
    () => deriveResourceSlots(participants, options),
    [participants, options],
  );

  const isForOneParticipant = participantIndex !== undefined;
  const visibleSlots = isForOneParticipant
    ? slots.filter((slot) => slot.participantIndex === participantIndex)
    : slots;

  const handleSelect = ({
    option,
    participantIndex,
    resourceAttributeId,
    resourceAttributeValueId,
  }: ResourceAttributePick) => {
    onChange(
      applyResourcePick({
        option,
        participantIndex,
        resourceAttributeId,
        resourceAttributeValueId,
        selections: value,
        slots,
      }),
    );
  };

  return (
    <SC.PickerList>
      {visibleSlots.map((slot) => (
        <SC.UnitBlock
          key={`resource-unit-${slot.participantIndex}`}
          className="resource-attribute-picker__unit"
        >
          {!isForOneParticipant && (
            <SC.UnitHeading>
              {translate('resourceUnitLabel', {
                tierName: translate(slot.priceTierName),
                number: slot.unitNumber,
              })}
            </SC.UnitHeading>
          )}
          {slot.options.map((option) => (
            <SC.ResourceGroup
              key={`${slot.participantIndex}-${option.resourcePoolId}`}
            >
              {slot.options.length > 1 && (
                <SC.ResourceName>{option.resourcePoolName}</SC.ResourceName>
              )}
              <SC.SelectGrid>
                {option.attributes.map((attribute, attributeIndex) => {
                  const pickParams = {
                    attributeIndex,
                    option,
                    participantIndex: slot.participantIndex,
                    selections: value,
                  };
                  return (
                    <ResourceAttributeSelect
                      key={attribute.resourceAttributeId}
                      attribute={attribute}
                      compatibleValueIds={deriveCompatibleValueIds(pickParams)}
                      earlierPickLabels={deriveEarlierPickLabels(pickParams)}
                      onSelect={handleSelect}
                      option={option}
                      participantIndex={slot.participantIndex}
                      portalContainer={portalContainer}
                      selectableValueIds={deriveSelectableValueIds({
                        ...pickParams,
                        slots,
                      })}
                      selectedValueId={
                        findResourcePick(
                          value[slot.participantIndex],
                          option.resourcePoolId,
                          attribute.resourceAttributeId,
                        )?.resourceAttributeValueId
                      }
                    />
                  );
                })}
              </SC.SelectGrid>
            </SC.ResourceGroup>
          ))}
        </SC.UnitBlock>
      ))}
    </SC.PickerList>
  );
};

export default ResourceAttributePicker;
